import {t} from "@lingui/core/macro";
import {StyleSheet} from "aphrodite";
import {useState} from "react";

import {View} from "@khanacademy/wonder-blocks-core";
import {TextField} from "@khanacademy/wonder-blocks-form";
import {LabeledField} from "@khanacademy/wonder-blocks-labeled-field";
import {sizing} from "@khanacademy/wonder-blocks-tokens";

interface Props {
    /**
     * The given name of the user.
     */
    firstName: string | null | undefined;
    /**
     * The family name of the user.
     */
    lastName: string | null | undefined;
    /**
     * Callback to set the first name.
     */
    setFirstName: (firstName: string) => void;
    /**
     * Callback to set the last name.
     */
    setLastName: (lastName: string) => void;
    /**
     * Callback after validating the first name.
     */
    onValidateFirstName?: (errorMessage?: string | null | undefined) => unknown;
    /**
     * Callback after validating the last name.
     */
    onValidateLastName?: (errorMessage?: string | null | undefined) => unknown;
    /**
     * Whether the fields are required.
     */
    required?: boolean;
    /**
     * Whether the "required" label is shown.
     */
    showRequiredLabel?: boolean;
}

/**
 * A set of fields for the user to enter their first and last name. This is a
 * component because we always ask for both names together.
 */
export function FullNameField({
    firstName,
    lastName,
    setFirstName,
    setLastName,
    onValidateFirstName,
    onValidateLastName,
    required,
    showRequiredLabel,
}: Props): React.ReactNode {
    const [firstNameErrorMessage, setFirstNameErrorMessage] = useState<
        string | null | undefined
    >(null);
    const [lastNameErrorMessage, setLastNameErrorMessage] = useState<
        string | null | undefined
    >(null);

    // If either first or last name is empty, set the same error message
    const validateFirstName = (value: string) => {
        setFirstNameErrorMessage(null);
        // If the field is required and the current value is empty
        if (required && !value.length) {
            return t`Please tell us your first name.`;
        }
    };

    const validateLastName = (value: string) => {
        setLastNameErrorMessage(null);
        // If the field is required and the current value is empty
        if (required && !value.length) {
            return t`Please tell us your last name.`;
        }
    };

    return (
        <View style={styles.inputsContainer}>
            <LabeledField
                label={t`First name`}
                contextLabel={showRequiredLabel ? t`required` : undefined}
                errorMessage={firstNameErrorMessage}
                testId="first-name-field"
                styles={{root: styles.fieldWrapper}}
                field={
                    <TextField
                        value={firstName ?? ""}
                        onChange={setFirstName}
                        validate={(value) => validateFirstName(value)}
                        onValidate={(errorMessage) => {
                            setFirstNameErrorMessage(errorMessage);
                            onValidateFirstName?.(errorMessage);
                        }}
                        required={required}
                    />
                }
            />
            <LabeledField
                label={t`Last name`}
                contextLabel={showRequiredLabel ? t`required` : undefined}
                errorMessage={lastNameErrorMessage}
                testId="last-name-field"
                styles={{root: styles.fieldWrapper}}
                field={
                    <TextField
                        value={lastName ?? ""}
                        onChange={setLastName}
                        validate={(value) => validateLastName(value)}
                        onValidate={(errorMessage) => {
                            setLastNameErrorMessage(errorMessage);
                            onValidateLastName?.(errorMessage);
                        }}
                        required={required}
                    />
                }
            />
        </View>
    );
}

const styles = StyleSheet.create({
    inputsContainer: {
        flexDirection: "row",
        gap: sizing.size_080,
        width: "100%",
    },
    fieldWrapper: {
        flex: 1,
    },
});
